import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { toast } from "sonner";

const CATEGORY_LABEL = {
  comprehension: "Comprehension",
  evidence: "Evidence",
  analysis: "Analysis",
  perspective: "Perspective",
  reflection: "Reflection",
};

function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
}

function ReaderContent({ session }) {
  const pages = session.document?.pages || [];
  if (pages.length > 0) {
    return (
      <article className="font-reading text-lg sm:text-xl leading-[1.85] text-forge-100/90 reading-prose" data-reader-mode="true">
        {pages.map((page) => (
          <section key={page.page_number} className="mb-12 scroll-mt-24" data-page-number={page.page_number}>
            <div className="mb-5 text-[10px] uppercase tracking-[0.3em] text-gold-500/70">
              Page {page.page_number}
            </div>
            {page.text.split(/\n{2,}/).map((para, i) => (
              <p key={`${page.page_number}-${i}`}>{para.trim()}</p>
            ))}
          </section>
        ))}
      </article>
    );
  }

  return (
    <article className="font-reading text-lg sm:text-xl leading-[1.85] text-forge-100/90 reading-prose" data-reader-mode="true">
      {session.content.split(/\n{2,}/).map((para, i) => (
        <p key={i}>{para.trim()}</p>
      ))}
    </article>
  );
}

function OriginalPdfView({ url, loading }) {
  return (
    <div className="space-y-4" data-testid="original-pdf-view">
      <div className="border border-white/10 rounded-sm overflow-hidden bg-forge-900">
        {url ? (
          <iframe
            title="Original PDF"
            src={url}
            className="w-full h-[72vh] min-h-[520px] bg-forge-100"
          />
        ) : (
          <div className="h-[52vh] min-h-[360px] flex items-center justify-center text-xs uppercase tracking-[0.3em] text-forge-100/45">
            {loading ? "Loading original PDF…" : "Original PDF unavailable"}
          </div>
        )}
      </div>
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="inline-block text-xs uppercase tracking-[0.25em] text-forge-100/60 hover:text-gold-400 transition-colors"
        >
          Open original PDF
        </a>
      )}
    </div>
  );
}

export default function Reading() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [vocab, setVocab] = useState(null);
  const [vocabLoading, setVocabLoading] = useState(false);
  const [questions, setQuestions] = useState(null);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [reflectionsSaved, setReflectionsSaved] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [journalPrompts, setJournalPrompts] = useState([]);
  const [journalEntries, setJournalEntries] = useState({});
  const [journalSaving, setJournalSaving] = useState(false);
  const [debateStarting, setDebateStarting] = useState(false);
  const [readerView, setReaderView] = useState("reader");
  const [originalPdfUrl, setOriginalPdfUrl] = useState(null);
  const [originalPdfLoading, setOriginalPdfLoading] = useState(false);
  const startRef = useRef(Date.now());
  const tickRef = useRef(null);

  useEffect(() => {
    api.get(`/sessions/${id}`).then(({ data }) => {
      setSession(data);
      if (data.vocabulary?.words) setVocab(data.vocabulary.words);
      if (data.questions?.questions) setQuestions(data.questions.questions);
      if (data.answers) {
        setAnswers(data.answers);
        if (Object.values(data.answers).some((v) => (v || "").trim())) setReflectionsSaved(true);
      }
      if (data.completed_at) {
        setCompleted(true);
        setElapsed(data.duration_seconds || 0);
      }
    }).catch(() => {
      toast.error("Could not load this reading.");
      navigate("/learn");
    });
    api.get("/journal/prompts").then(({ data }) => setJournalPrompts(data)).catch(() => {});
    api.get(`/sessions/${id}/journal`).then(({ data }) => {
      if (data?.entries) setJournalEntries(data.entries);
    }).catch(() => {});
  }, [id, navigate]);

  useEffect(() => {
    if (completed) return;
    startRef.current = Date.now();
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [completed]);

  useEffect(() => {
    return () => {
      if (originalPdfUrl) URL.revokeObjectURL(originalPdfUrl);
    };
  }, [originalPdfUrl]);

  const canViewOriginalPdf = Boolean(
    session?.source === "upload" &&
    session?.source_id &&
    session?.document?.has_original &&
    session?.document?.mime_type === "application/pdf"
  );

  const showOriginalPdf = async () => {
    if (!canViewOriginalPdf) return;
    setReaderView("original");
    if (originalPdfUrl || originalPdfLoading) return;
    setOriginalPdfLoading(true);
    try {
      const { data } = await api.get(`/documents/${session.source_id}/original`, { responseType: "blob" });
      setOriginalPdfUrl(URL.createObjectURL(new Blob([data], { type: "application/pdf" })));
    } catch {
      toast.error("Could not load the original PDF.");
      setReaderView("reader");
    } finally {
      setOriginalPdfLoading(false);
    }
  };

  const loadVocab = async () => {
    if (vocab) return;
    setVocabLoading(true);
    try {
      const { data } = await api.post(`/sessions/${id}/vocabulary`);
      setVocab(data.words || []);
    } catch {
      toast.error("Vocabulary mentor unavailable.");
    } finally {
      setVocabLoading(false);
    }
  };

  const loadQuestions = async () => {
    if (questions) return;
    setQuestionsLoading(true);
    try {
      const { data } = await api.post(`/sessions/${id}/questions`);
      setQuestions(data.questions || []);
    } catch {
      toast.error("Intellectual mentor unavailable.");
    } finally {
      setQuestionsLoading(false);
    }
  };

  const completeReading = async () => {
    if (completed || completing) return;
    setCompleting(true);
    try {
      await api.post("/sessions/complete", { session_id: id, duration_seconds: elapsed });
      setCompleted(true);
      clearInterval(tickRef.current);
      // Auto-load mentors
      await loadVocab();
      await loadQuestions();
      toast.success("Reading recorded. Now decode and reflect.");
    } catch {
      toast.error("Could not record completion.");
    } finally {
      setCompleting(false);
    }
  };

  const saveReflections = async () => {
    setSaving(true);
    try {
      await api.post(`/sessions/${id}/reflections`, { session_id: id, answers });
      toast.success("Reflections saved.");
      setReflectionsSaved(true);
    } catch {
      toast.error("Could not save reflections.");
    } finally {
      setSaving(false);
    }
  };

  const saveJournal = async () => {
    setJournalSaving(true);
    try {
      await api.post(`/sessions/${id}/journal`, { entries: journalEntries });
      toast.success("Journal entry saved.");
    } catch {
      toast.error("Could not save journal entry.");
    } finally {
      setJournalSaving(false);
    }
  };

  const openDebate = async () => {
    setDebateStarting(true);
    try {
      const { data } = await api.post(`/sessions/${id}/debate/start`);
      navigate(`/debate/${data.debate_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open the debate.");
    } finally {
      setDebateStarting(false);
    }
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-forge-900 text-forge-100 flex items-center justify-center">
        <div className="text-sm tracking-[0.3em] uppercase text-ember-500 animate-pulse">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />

      {/* Reading canvas */}
      <section className="bg-[#141312] border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-16 sm:py-24 relative">
          <div className="absolute top-6 right-6 font-mono text-xs text-forge-100/40" data-testid="reading-timer">
            {fmt(elapsed)}
          </div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-5">{session.source === "ai" ? "AI Essay" : session.source === "library" ? "Library" : "Your Upload"}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.1] mb-12 text-balance" data-testid="reading-title">
            {session.title}
          </h1>
          {canViewOriginalPdf && (
            <div className="flex flex-wrap gap-2 mb-10" data-testid="reader-mode-toggle">
              <button
                type="button"
                onClick={() => setReaderView("reader")}
                className={`px-4 py-2 text-xs uppercase tracking-[0.25em] border rounded-sm transition-all ${readerView === "reader" ? "border-ember-500 text-ember-400 bg-forge-900" : "border-white/15 text-forge-100/55 hover:border-ember-500/60 hover:text-ember-400"}`}
              >
                Reader Mode
              </button>
              <button
                type="button"
                onClick={showOriginalPdf}
                className={`px-4 py-2 text-xs uppercase tracking-[0.25em] border rounded-sm transition-all ${readerView === "original" ? "border-gold-500 text-gold-400 bg-forge-900" : "border-white/15 text-forge-100/55 hover:border-gold-500/60 hover:text-gold-400"}`}
              >
                View Original PDF
              </button>
            </div>
          )}

          {readerView === "original" && canViewOriginalPdf ? (
            <OriginalPdfView url={originalPdfUrl} loading={originalPdfLoading} />
          ) : (
            <ReaderContent session={session} />
          )}

          <div className="mt-16 flex flex-wrap items-center gap-4">
            {!completed ? (
              <button
                onClick={completeReading}
                disabled={completing}
                className="px-7 py-3 bg-ember-500 hover:bg-ember-400 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
                data-testid="reading-complete-btn"
              >
                {completing ? "Recording…" : "I've finished reading"}
              </button>
            ) : (
              <div className="text-xs uppercase tracking-[0.3em] text-gold-500" data-testid="reading-completed-badge">
                ✓ Reading recorded · {fmt(elapsed)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* VOCABULARY MENTOR */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-16 sm:py-20">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-2">Vocabulary Mentor</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Words worth knowing.</h2>
          </div>
          {!vocab && (
            <button
              onClick={loadVocab}
              disabled={vocabLoading}
              className="text-xs uppercase tracking-[0.25em] px-4 py-2 border border-white/15 hover:border-ember-500 hover:text-ember-400 transition-all rounded-sm"
              data-testid="load-vocab-btn"
            >
              {vocabLoading ? "Forging…" : "Reveal Vocabulary"}
            </button>
          )}
        </div>
        {vocab && vocab.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-px bg-white/5" data-testid="vocab-list">
            {vocab.map((w, i) => (
              <div key={i} className="bg-forge-900 p-6 hover:bg-forge-800 transition-colors">
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <span className="font-serif text-2xl text-ember-400">{w.word}</span>
                  {w.pronunciation && <span className="font-mono text-xs text-forge-100/40">/{w.pronunciation}/</span>}
                </div>
                {w.definition && <p className="text-sm text-forge-100/90 mb-2"><span className="text-forge-100/50">def.</span> {w.definition}</p>}
                {w.explanation && <p className="text-sm text-forge-100/70 italic mb-3">{w.explanation}</p>}
                {w.example && <p className="text-sm text-forge-100/60 border-l border-gold-500/40 pl-3 mb-4">{w.example}</p>}
                {(w.root || w.prefix || w.suffix || w.origin || (w.related && w.related.length)) && (
                  <div className="mt-3 pt-4 border-t border-white/5 space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-gold-500">Root Word Explorer</div>
                    <div className="grid grid-cols-1 gap-1 text-xs text-forge-100/70">
                      {w.root && <div><span className="text-forge-100/40 uppercase tracking-wider mr-2">Root</span>{w.root}</div>}
                      {w.prefix && <div><span className="text-forge-100/40 uppercase tracking-wider mr-2">Prefix</span>{w.prefix}</div>}
                      {w.suffix && <div><span className="text-forge-100/40 uppercase tracking-wider mr-2">Suffix</span>{w.suffix}</div>}
                      {w.origin && <div><span className="text-forge-100/40 uppercase tracking-wider mr-2">Origin</span>{w.origin}</div>}
                      {w.related && w.related.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          {w.related.map((r, ri) => (
                            <span key={ri} className="px-2 py-0.5 border border-white/10 rounded-sm text-[11px] text-forge-100/70">{r}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : vocab && (
          <p className="text-sm text-forge-100/40">No challenging vocabulary surfaced.</p>
        )}
      </section>

      {completed ? (
        <>
      {/* INTELLECTUAL MENTOR */}
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16 sm:py-20 border-t border-white/5">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-2">Intellectual Mentor</div>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">Five questions to sharpen your thought.</h2>
          </div>
          {!questions && (
            <button
              onClick={loadQuestions}
              disabled={questionsLoading}
              className="text-xs uppercase tracking-[0.25em] px-4 py-2 border border-white/15 hover:border-ember-500 hover:text-ember-400 transition-all rounded-sm"
              data-testid="load-questions-btn"
            >
              {questionsLoading ? "Forging…" : "Reveal Questions"}
            </button>
          )}
        </div>
        {questions && (
          <div className="space-y-10" data-testid="questions-list">
            {questions.map((q, i) => (
              <div key={i}>
                <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-2">{CATEGORY_LABEL[q.category] || q.category}</div>
                <h3 className="font-serif text-xl sm:text-2xl text-forge-100 leading-snug mb-4">{q.prompt}</h3>
                <textarea
                  value={answers[q.category] || ""}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.category]: e.target.value }))}
                  placeholder="Write your reflection…"
                  rows={4}
                  className="w-full bg-transparent border-b border-white/10 focus:border-ember-500 outline-none py-3 text-forge-100 placeholder-forge-100/30 font-reading text-lg leading-relaxed transition-colors resize-none"
                  data-testid={`answer-${q.category}`}
                />
              </div>
            ))}
            <div className="flex items-center gap-4 pt-4 flex-wrap">
              <button
                onClick={saveReflections}
                disabled={saving}
                className="px-6 py-2.5 bg-ember-500 hover:bg-ember-400 disabled:opacity-60 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
                data-testid="save-reflections-btn"
              >
                {saving ? "Saving…" : "Save Reflections"}
              </button>
              <button
                onClick={openDebate}
                disabled={!reflectionsSaved || debateStarting}
                className="px-6 py-2.5 border border-ember-500/60 hover:border-ember-500 hover:bg-forge-800 text-ember-400 disabled:opacity-40 disabled:cursor-not-allowed text-sm tracking-wide rounded-sm transition-all"
                title={!reflectionsSaved ? "Save at least one reflection to open the debate." : ""}
                data-testid="open-debate-btn"
              >
                {debateStarting ? "Opening dialogue…" : "Open Debate Mentor →"}
              </button>
              <Link
                to={`/research?session_id=${id}`}
                className="px-6 py-2.5 border border-gold-500/60 hover:border-gold-500 hover:bg-forge-800 text-gold-400 text-sm tracking-wide rounded-sm transition-all"
                data-testid="open-research-btn"
              >
                Open Research Mentor →
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* INTELLECTUAL JOURNAL */}
      <section id="journal" className="max-w-3xl mx-auto px-6 lg:px-10 py-16 sm:py-20 border-t border-white/5">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-2">Intellectual Journal</div>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-3">Write to yourself.</h2>
        <p className="text-sm text-forge-100/60 mb-10 max-w-xl">
          Short, honest notes. Future-you will be grateful you wrote them.
        </p>
        <div className="space-y-8" data-testid="journal-form">
          {journalPrompts.map((p) => (
            <div key={p.key}>
              <div className="text-[10px] tracking-[0.3em] uppercase text-ember-500 mb-2">{p.prompt}</div>
              <textarea
                rows={3}
                value={journalEntries[p.key] || ""}
                onChange={(e) => setJournalEntries((j) => ({ ...j, [p.key]: e.target.value }))}
                placeholder="A sentence or two is enough."
                className="w-full bg-transparent border-b border-white/10 focus:border-gold-500 outline-none py-2 text-forge-100 placeholder-forge-100/30 font-reading text-base leading-relaxed transition-colors resize-none"
                data-testid={`journal-${p.key}`}
              />
            </div>
          ))}
          <div className="flex items-center gap-4 pt-2 flex-wrap">
            <button
              onClick={saveJournal}
              disabled={journalSaving}
              className="px-6 py-2.5 bg-gold-500 hover:bg-gold-400 disabled:opacity-60 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
              data-testid="save-journal-btn"
            >
              {journalSaving ? "Saving…" : "Save Journal Entry"}
            </button>
            <button
              onClick={() => navigate("/journal")}
              className="text-xs uppercase tracking-[0.25em] text-forge-100/60 hover:text-gold-400 transition-colors"
              data-testid="view-journal-link"
            >
              View Journal →
            </button>
          </div>
        </div>
      </section>
        </>
      ) : (
        <PostReadingLocked onComplete={completeReading} completing={completing} />
      )}
    </div>
  );
}

function PostReadingLocked({ onComplete, completing }) {
  return (
    <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16 sm:py-20 border-t border-white/5">
      <div className="border border-dashed border-white/15 rounded-sm p-8 sm:p-10">
        <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-3">Next Step</div>
        <h2 className="font-serif text-3xl sm:text-4xl tracking-tight mb-3">Mark the reading complete.</h2>
        <p className="text-sm text-forge-100/60 leading-relaxed mb-6 max-w-xl">
          Reflections, Debate Mentor, the Report Card, and Journal open after the reading is recorded.
        </p>
        <button
          onClick={onComplete}
          disabled={completing}
          className="px-6 py-2.5 bg-ember-500 hover:bg-ember-400 disabled:opacity-60 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
          data-testid="post-reading-complete-btn"
        >
          {completing ? "Recording…" : "Mark Reading Complete"}
        </button>
      </div>
    </section>
  );
}
